import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import ImageScroller from '../imagescroller';
import LazyImage from '../gui/lazyImage';

import { uploadImageFile } from '../../lib/pasteImageUpload';
import { isConventionMember, isConventionOrganizer, isClubRangeOfficer } from '../../lib/conventionResults';

/**
 * User-contributed event photos, shown at the bottom of the convention page.
 *
 * Any logged-in user can add a photo ("Add photo"); each photo carries an
 * "Add to feed" checkbox (toggleable by the uploader and by convention
 * officials). Flagged photos are pre-attached as images when an official
 * publishes the recap/summary feed article (postRecapToFeed in view.js).
 * Photos live on convention.eventPhotos — separate from convention.images
 * (the hero gallery) — and are managed by /api/conventions/:id/photos.
 */
@observer
class ConventionEventPhotos extends Component {
    constructor(props) {
        super(props);
        this.state = {};
        this.fileInput = null;
    }

    pickFile = () => {
        if (this.fileInput) {
            this.fileInput.click();
        }
    }

    pickCamera = () => {
        if (this.cameraInput) {
            this.cameraInput.click();
        }
    }

    // Tap a thumbnail → large swipeable viewer (ImageScroller, with its own
    // fullscreen button) above the grid, opened at that photo.
    openViewer = idx => {
        this.setState({ viewerIdx: idx });
    }

    closeViewer = () => {
        this.setState({ viewerIdx: null });
    }

    // Upload every picked file sequentially. Files that fail are collected and
    // reported together; the ones that succeeded stay uploaded.
    onFileChange = async e => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // allow picking the same file(s) again
        if (files.length === 0) {
            return;
        }
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        this.setState({ uploading: true, uploadError: null, uploadCurrent: 0, uploadTotal: files.length });
        const failed = [];
        for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            this.setState({ uploadCurrent: i + 1 });
            try {
                // eslint-disable-next-line no-await-in-loop
                const uploaded = await uploadImageFile(file);
                // eslint-disable-next-line no-await-in-loop
                const response = await util.fetchApi(`/api/conventions/${convention.id}/photos`, { method: 'POST' }, {
                    ...uploaded,
                });
                if (response && response.status === 201 && response.data && response.data.photo) {
                    const existing = Array.isArray(convention.eventPhotos) ? convention.eventPhotos : [];
                    conventionStore.updateObjectKeyValue('convention', 'eventPhotos', [...existing, response.data.photo]);
                } else {
                    failed.push(file.name);
                }
            } catch (err) {
                failed.push(file.name);
            }
        }
        this.setState({
            uploading: false,
            uploadError: failed.length > 0 ? failed.join(', ') : null,
        });
    }

    toggleFeed = async photo => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const addToFeed = !photo.addToFeed;
        const response = await util.fetchApi(`/api/conventions/${convention.id}/photos/${photo.md5}`, { method: 'PATCH' }, {
            addToFeed,
        });
        if (response && (response.status === 202 || response.status === 200)) {
            const updated = (convention.eventPhotos || []).map(p => (p && p.md5 === photo.md5 ? { ...p, addToFeed } : p));
            conventionStore.updateObjectKeyValue('convention', 'eventPhotos', updated);
        }
    }

    deletePhoto = async photo => {
        const { conventionStore } = this.props.stores;
        const { convention } = conventionStore;
        const response = await util.fetchApi(`/api/conventions/${convention.id}/photos/${photo.md5}`, { method: 'DELETE' }, {});
        if (response && (response.status === 202 || response.status === 200)) {
            const updated = (convention.eventPhotos || []).filter(p => !p || p.md5 !== photo.md5);
            conventionStore.updateObjectKeyValue('convention', 'eventPhotos', updated);
        }
    }

    render() {
        const { userStore, conventionStore, appState } = this.props.stores;
        const { user = {}, isAdmin, isCustomerAdmin, isRangeOfficer } = userStore;
        const { isMobile } = appState;
        const { convention } = conventionStore;
        const { uploading, uploadError, uploadCurrent, uploadTotal, viewerIdx } = this.state;

        if (!convention || !convention.id) {
            return null;
        }
        const photos = Array.isArray(convention.eventPhotos) ? convention.eventPhotos : [];
        const isOfficial = isAdmin || isCustomerAdmin
            || (!!user.id && convention.user === user.id)
            || isConventionMember(convention, user)
            || isConventionOrganizer(convention, user)
            || isClubRangeOfficer(convention, user, isRangeOfficer);
        const canModify = photo => !!user.id && (photo.uploadedBy === user.id || isOfficial);

        // Time-aware nudge: recruit photographers before the event, ask for
        // photos from the moment it starts. No startDate = treat as started.
        const hasStarted = !convention.startDate || new Date(convention.startDate) <= new Date();

        // Social proof: unique contributors, thanked by first name.
        const contributors = [...new Set(photos
            .map(p => (p && p.uploadedByName ? String(p.uploadedByName).trim() : ''))
            .filter(name => name))];
        const firstNames = contributors.map(name => name.split(' ')[0]);
        let thanksNames = '';
        if (firstNames.length > 5) {
            thanksNames = `${firstNames.slice(0, 5).join(', ')} + ${firstNames.length - 5}`;
        } else if (firstNames.length > 1) {
            thanksNames = `${firstNames.slice(0, -1).join(', ')} og ${firstNames[firstNames.length - 1]}`;
        } else if (firstNames.length === 1) {
            thanksNames = firstNames[0];
        }

        return (
            <div class='px-3 mt-4 mb-2'>
                {photos.length > 0 && <>
                    <div class='mb-2' style='font-size: 1rem; font-weight: 500;'>
                        <i class='fa-duotone fa-images me-2' />
                        <Text id='convention.event-photos-title'>Photos from the event</Text>
                        <span class='text-body-secondary ms-2' style='font-size: 0.8rem;'>({photos.length})</span>
                    </div>
                    {viewerIdx !== null && viewerIdx !== undefined && photos[viewerIdx] && (
                        <div class='position-relative mb-3'>
                            <ImageScroller
                                key={`event-photo-viewer-${viewerIdx}`}
                                images={photos}
                                imageIdx={viewerIdx}
                                stores={this.props.stores}
                                showImg={img => img.s3LargeLink || img.s3MediumLink || img.s3Link}
                                showImageInfo={false}
                                maxHeight={'60vh'}
                            />
                            {/* right: 52px keeps this clear of the expand button
                                ImageScroller renders at top: 15px; right: 15px */}
                            <button
                                type='button'
                                class='btn btn-sm btn-dark rounded-circle position-absolute'
                                style='top: 10px; right: 52px; z-index: 10; opacity: 0.8;'
                                onClick={this.closeViewer}
                            >
                                <i class='fa-solid fa-xmark' />
                            </button>
                        </div>
                    )}
                    <div class='d-flex flex-wrap gap-2 mb-2'>
                        {photos.map((photo, photoIdx) => (
                            <div key={photo.md5} class='card' style='width: 150px;'>
                                {/* LazyImage retries with cache-busting until the server has
                                    generated the resized variant — a freshly uploaded photo's
                                    thumbnail appears as soon as it is ready, like every other
                                    image upload in the app. */}
                                <LazyImage
                                    className='card-img-top overflow-hidden'
                                    src={photo.s3SmallLink || photo.s3MediumLink || photo.s3Link}
                                    lowResBase64={photo.lowResBase64}
                                    alt={photo.name || ''}
                                    loading='lazy'
                                    style='height: 100px; object-fit: cover; cursor: pointer;'
                                    onClick={() => this.openViewer(photoIdx)}
                                />
                                <div class='card-body p-1'>
                                    <div class='text-body-secondary text-truncate' style='font-size: 0.65rem;'>
                                        {photo.uploadedByName || ''}
                                    </div>
                                    <div class='d-flex align-items-center justify-content-between'>
                                        <label class='d-flex align-items-center mb-0' style={`font-size: 0.7rem; ${canModify(photo) ? 'cursor: pointer;' : ''}`}>
                                            <input
                                                type='checkbox'
                                                class='form-check-input me-1 mt-0'
                                                checked={!!photo.addToFeed}
                                                disabled={!canModify(photo)}
                                                onChange={() => this.toggleFeed(photo)}
                                            />
                                            <Text id='convention.photo-add-to-feed'>Add to feed</Text>
                                        </label>
                                        {canModify(photo) && (
                                            <i
                                                class='fa-duotone fa-trash text-danger ms-1'
                                                style='cursor: pointer; font-size: 0.75rem;'
                                                onClick={() => this.deletePhoto(photo)}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {thanksNames && (
                        <div class='text-body-secondary mb-2' style='font-size: 0.85rem;'>
                            <i class='fa-duotone fa-heart me-1 text-danger' />
                            <Text id='convention.photo-thanks' fields={{ names: thanksNames }}>Thanks to {thanksNames} for the photos!</Text>
                        </div>
                    )}
                </>}
                {uploadError && (
                    <div class='alert alert-danger py-2' style='font-size: 0.85rem;'>
                        <Text id='convention.photo-upload-failed'>Photo upload failed</Text>: {uploadError}
                    </div>
                )}
                {/* Invitation card: always visible so the ask is never hidden —
                    logged-out users get a login hint instead of the buttons. */}
                <div class='card mt-2'>
                    <div class='card-body text-center py-4'>
                        <i class='fa-duotone fa-camera-retro text-primary mb-2' style='font-size: 2.5rem;' />
                        <div class='fw-bold' style='font-size: 1.1rem;'>
                            {hasStarted ? <>
                                <Text id='convention.photo-hero-title'>Share your photos from the event!</Text>
                            </> : <>
                                <Text id='convention.photo-hero-title-before'>Become the event photographer! 📸</Text>
                            </>}
                        </div>
                        <div class='text-body-secondary mb-3' style='font-size: 0.9rem;'>
                            {!hasStarted && <>
                                <Text id='convention.photo-hero-text-before'>Bring your camera — the photos end up here.</Text>
                            </>}
                            {hasStarted && photos.length === 0 && <>
                                <Text id='convention.photo-hero-text-empty'>No photos yet — be the first!</Text>
                            </>}
                            {hasStarted && photos.length === 1 && <>
                                <Text id='convention.photo-hero-text-one'>One photo so far — add more!</Text>
                            </>}
                            {hasStarted && photos.length > 1 && contributors.length > 1 && <>
                                <Text
                                    id='convention.photo-hero-text-count'
                                    fields={{ photoCount: photos.length, contributorCount: contributors.length }}
                                >
                                    {photos.length} photos from {contributors.length} participants — add yours!
                                </Text>
                            </>}
                            {hasStarted && photos.length > 1 && contributors.length <= 1 && <>
                                <Text
                                    id='convention.photo-hero-text-count-single'
                                    fields={{ photoCount: photos.length }}
                                >
                                    {photos.length} photos so far — add yours!
                                </Text>
                            </>}
                        </div>
                        {user.id ? <>
                            <input
                                type='file'
                                accept='image/*'
                                multiple
                                style='display: none;'
                                ref={c => this.fileInput = c}
                                onChange={this.onFileChange}
                            />
                            <input
                                type='file'
                                accept='image/*'
                                capture='environment'
                                style='display: none;'
                                ref={c => this.cameraInput = c}
                                onChange={this.onFileChange}
                            />
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button
                                    type='button'
                                    class='btn btn-primary rounded-pill px-4'
                                    onClick={this.pickFile}
                                    disabled={uploading}
                                >
                                    {uploading ? <>
                                        <i class='fa-duotone fa-spinner fa-spin me-2' />
                                        <Text
                                            id='convention.photo-uploading-progress'
                                            fields={{ current: uploadCurrent, total: uploadTotal }}
                                        >
                                            Uploading {uploadCurrent} of {uploadTotal}…
                                        </Text>
                                    </> : <>
                                        <i class='fa-duotone fa-images me-2' />
                                        <Text id='convention.photo-pick'>Choose photos</Text>
                                    </>}
                                </button>
                                {isMobile && <>
                                    <button
                                        type='button'
                                        class='btn btn-outline-primary rounded-pill px-4'
                                        onClick={this.pickCamera}
                                        disabled={uploading}
                                    >
                                        <i class='fa-duotone fa-camera me-2' />
                                        <Text id='convention.photo-take'>Take a photo</Text>
                                    </button>
                                </>}
                            </div>
                        </> : <>
                            <div class='text-body-secondary' style='font-size: 0.9rem;'>
                                <i class='fa-duotone fa-right-to-bracket me-1' />
                                <Text id='convention.photo-login-hint'>Log in to share photos</Text>
                            </div>
                        </>}
                    </div>
                </div>
            </div>
        );
    }
}

export default ConventionEventPhotos;
